// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState, useEffect } from 'react';
import { z } from 'zod';
import { Container, Box, Typography, TextField, Button } from '@mui/material';
import Copyright from '../components/Copyright';
import { useBranding } from '../contexts/BrandingContext';
import { useUserState } from '../contexts/UserContext';
import { useConfig } from '../hooks/other/useConfig';

const Alphanumeric = z
  .string()
  .refine((value) => /^[a-zA-Z0-9]+$/.test(value), 'Name contains only alphanumeric characters');

function validate(e: string) {
  return !!Alphanumeric.safeParse(e).success;
}

const Login: React.FC = () => {
  const config = useConfig();

  const [userId, setUserId] = useState<string>('');
  const { login, promptsForUserId } = useUserState();
  const { branding } = useBranding();

  const [showUserIdAsTitle] = useState(false);

  const loginKey = (key: string) => {
    if (validate(userId) && key === 'Enter') {
      login(userId);
    }
  };

  // If you want to display the user id in the title, e.g., for demo purposes, simply set the
  // showUserIdAsTitle state to true above.
  useEffect(() => {
    if (userId && showUserIdAsTitle) {
      document.title = userId.toUpperCase();
    }
  }, [userId, showUserIdAsTitle]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: '90vh',
      }}
    >
      <Container
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '90vh',
          pl: 50,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {branding.loginLogo}
          <Typography component="h1" variant="h4" sx={{ mt: 5 }}>
            {config.branding.app_name || 'Utility'}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {promptsForUserId() ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="login-userid-input"
                  placeholder="User Id"
                  name="userid"
                  autoComplete="username"
                  autoFocus
                  onChange={(event) => setUserId(event.target.value)}
                  onKeyDown={(event) => loginKey(event.key)}
                />
                <Button
                  type="submit"
                  fullWidth
                  disabled={!validate(userId)}
                  sx={{ mt: 3, mb: 2 }}
                  id="login-button"
                  onClick={() => login(userId)}
                >
                  Log In
                </Button>
              </>
            ) : (
              <Button
                type="submit"
                fullWidth
                sx={{ mt: 3, mb: 2 }}
                id="login-button"
                autoFocus
                onClick={() => login('')}
              >
                Log In
              </Button>
            )}
          </Box>
          <Copyright />
        </Box>
      </Container>
    </Container>
  );
};

export default Login;
